'use client'

import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { ExpandMoreIcon } from '@repo/ui/ExpandMoreIcon'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { FolderIcon } from '@repo/ui/FolderIcon'
import { useCallback, useEffect, useState } from 'react'
import { useMatterHistoryStore } from '@repo/store/useMatterHistoryStore'
import { useTheme } from '@mui/material/styles'
import MUIClickAwayListener from '@mui/material/ClickAwayListener'
import MUIFormControl from '@mui/material/FormControl'
import MUIIconButton from '@mui/material/IconButton'
import MUIInputAdornment from '@mui/material/InputAdornment'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUISelect from '@mui/material/Select'
import MUITypography from '@mui/material/Typography'
import type { SelectChangeEvent } from '@mui/material/Select'
import { transform } from 'typescript'

const LABEL_ID = 'matter-history-folder-label'

type MatterHistoryFolder = {
  bundleName: string
  bundleUserId: string
  caseNumber: string
  courtName: string
  createdBy: string
  createdDate: string
  hasChildren: boolean
  hearingNameAndDate: string
  lastModifiedBy: string
  lastModifiedDate: string
  matterHistoryFolderId: string
  matterHistoryFolderParentId: string
  name: string
  parentFolderPosition: number
  parties: string
  sectionLetter: string
  workTypeId: string
}

type MatterHistoryFolderSelectProps = {
  feeEarners_DI?: Array<any>
  multiple?: boolean
  onChange: (value: string | null) => void
  sx?: object
  value: string | null
  width?: string
  workTypeId?: string
}

export const MatterHistoryFolderSelect = ({
  workTypeId,
  value,
  onChange,
  width = '320px',
  sx
}: MatterHistoryFolderSelectProps) => {
  const [open, setOpen] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const [selectedFolderName, setSelectedFolderName] = useState<string | null>(null)
  const [parentHistory, setParentHistory] = useState<(string | null)[]>([null])

  const { fetchAllByMatterHistoryFolderParentId, matterHistoryFolders } = useMatterHistoryStore()

  const theme = useTheme()

  const fetchFolders = useCallback(
    async (parentId: string | null): Promise<MatterHistoryFolder[]> => {
      if (!workTypeId) return []

      const result = await fetchAllByMatterHistoryFolderParentId({
        matterHistoryFolderParentId: parentId,
        workTypeId: workTypeId ?? null
      })

      return Array.isArray(result) ? (result as MatterHistoryFolder[]) : []
    },
    [fetchAllByMatterHistoryFolderParentId, workTypeId]
  )

  useEffect(() => {
    if (!workTypeId) return
    void fetchFolders(null)
  }, [workTypeId, fetchFolders])

  const handleSelectChange = (_e: SelectChangeEvent<string>) => {
    // no-op on purpose. used to suppress MUI warning
  }

  const handleOpen = async () => {
    setShouldClose(false)

    onChange(null)
    setSelectedFolderName(null)
    setParentHistory([null])

    await fetchFolders(null)

    setOpen(true)
  }

  const handleClose = () => {
    if (shouldClose) {
      setOpen(false)
      setShouldClose(false)
    }
  }

  const handleLeafSelect = (folder: MatterHistoryFolder) => {
    onChange(folder.matterHistoryFolderId)
    setSelectedFolderName(folder.name)
    setShouldClose(true)
    setOpen(false)
  }

  const handleFolderClick = async (folder: MatterHistoryFolder) => {
    const children = await fetchFolders(folder.matterHistoryFolderId)

    if (!children || children.length === 0) {
      handleLeafSelect(folder)
    } else {
      setParentHistory((prev) => [...prev, folder.matterHistoryFolderId])
    }
  }

  const handleBack = async () => {
    if (parentHistory.length <= 1) {
      setOpen(false)
      onChange(null)
      setSelectedFolderName(null)
      setParentHistory([null])
      return
    }

    const newHistory = parentHistory.slice(0, -1)
    setParentHistory(newHistory)

    const previousParentId = newHistory[newHistory.length - 1]
    await fetchFolders(previousParentId)
  }

  const handleClickAway = () => {
    if (open) {
      setOpen(false)
      onChange(null)
      setSelectedFolderName(null)
      setParentHistory([null])
    }
  }

  return (
    <MUIClickAwayListener onClickAway={handleClickAway}>
      <MUIFormControl sx={{ width: '100%', ...sx }} variant="outlined">
        <MUIInputLabel id={LABEL_ID}>Folder</MUIInputLabel>
        <MUISelect
          labelId={LABEL_ID}
          label="Folder"
          multiple={false}
          value={value ?? ''}
          onChange={handleSelectChange}
          sx={{ width: '100%' }}
          open={open}
          onOpen={() => void handleOpen()}
          onClose={handleClose}
          renderValue={() => selectedFolderName || ''}
          MenuProps={{
            disableAutoFocus: true,
            onClose: (_event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                setOpen(false)
                onChange(null)
                setSelectedFolderName(null)
                setParentHistory([null])
              }
            }
          }}
          endAdornment={
            value ? (
              <MUIInputAdornment sx={{ marginRight: '10px' }} position="end">
                <MUIIconButton
                  onClick={() => {
                    setShouldClose(false)
                    onChange(null)
                    setSelectedFolderName(null)
                  }}
                >
                  <CloseIcon sx={{ width: '0.7rem', height: '0.7rem', mr: 1 }} />
                </MUIIconButton>
              </MUIInputAdornment>
            ) : null
          }
        >
          {matterHistoryFolders?.[0]?.matterHistoryFolderParentId && (
            <MUIMenuItem
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                void handleBack()
              }}
              sx={{
                margin: '6px 6px',
                borderRadius: '4px',
                cursor: 'pointer',
                width: `calc(${width} - 50px)`,
                fontWeight: 'bold'
              }}
            >
              <ExpandMoreIcon
                sx={{
                  color: theme.palette.grey[500],
                  mr: 1,
                  transform: 'rotate(90deg)',
                  width: '12px',
                  height: '12px'
                }}
              />
              <MUITypography
                sx={{
                  textTransform: 'uppercase',
                  mb: 0,
                  ml: 1,
                  fontSize: '0.75rem',
                  pointerEvents: 'none',
                  color: 'text.secondary'
                }}
              >
                Back
              </MUITypography>
            </MUIMenuItem>
          )}

          {matterHistoryFolders && matterHistoryFolders.length > 0 ? (
            matterHistoryFolders.map((folder: MatterHistoryFolder) => (
              <MUIMenuItem
                key={folder.matterHistoryFolderId}
                value={folder.matterHistoryFolderId}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  void handleFolderClick(folder)
                }}
                sx={{
                  margin: '6px 6px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: `calc(${width} - 50px)`
                }}
              >
                <FolderIcon sx={{ marginRight: '8px' }} />
                {folder.name}
                {folder.hasChildren && (
                  <ExpandMoreIcon
                    sx={{
                      color: theme.palette.grey[500],
                      mr: 1,
                      transform: 'rotate(270deg)',
                      width: '12px',
                      height: '12px',
                      position: 'absolute',
                      right: '.50rem'
                    }}
                  />
                )}
              </MUIMenuItem>
            ))
          ) : (
            <MUIMenuItem
              key={'no-folders'}
              value={null as any}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                void handleFolderClick(null as any)
              }}
              sx={{
                margin: '6px 6px',
                borderRadius: '4px',
                cursor: 'pointer',
                width: `calc(${width} - 50px)`
              }}
            >
              No folders found for this matter
            </MUIMenuItem>
          )}
        </MUISelect>
      </MUIFormControl>
    </MUIClickAwayListener>
  )
}
